import { Button, LinkIcon, UploadIcon } from "@minervaproject/fds";
import { ChangeEvent, useContext, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AddUrlForm } from "./add_url_form";

import { createResourceHelper } from "api/api_helpers";
import {
  createFeatureResourceToast,
  createFileUploadingToast,
  createResourceUploadedToast,
} from "components/shared/toasts";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProfileProperties } from "hooks/use_user_properties";
import { featureContentInOneUp } from "shared/reducers/classroom";
import { CapabilityType, PaneContentType, ResourceType, ResourceTypeType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";

const ALLOWED_FILE_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".bmp", ".mp4"];

function NewUrlAndFileResources() {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddUrlForm, setShowAddUrlForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUser = useUserProfileProperties(currentUserId);
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);

  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());

  if (!currentUser) {
    return null;
  }

  const handleUploadFile = async (event: ChangeEvent) => {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;

    try {
      setIsLoading(true);
      const file = files[0];
      const toastId = createFileUploadingToast(currentUser, file.name);
      const resource = await createResourceHelper(
        classId,
        ResourceTypeType.File,
        dispatch,
        forumAPI,
        {
          file,
          precreated: true,
        },
      );
      setIsLoading(false);
      if (toastId) toast.dismiss(toastId);
      if (resource) showFeatureResourceNotification(resource);
    } catch (e) {
      setIsLoading(false);
      window.flightLogger.error("[Resources] handleUploadFile failed");
      throw e;
    }
  };

  const handleAddUrl = async (validUrl: string) => {
    setShowAddUrlForm(false);

    const resourceType =
      validUrl.indexOf("youtube.com") > -1 || validUrl.indexOf("youtu.be") > -1
        ? ResourceTypeType.Video
        : ResourceTypeType.Url;

    try {
      setIsLoading(true);
      // TODO: set is-public using section.use_public_resources
      const resource = await createResourceHelper(classId, resourceType, dispatch, forumAPI, {
        url: validUrl,
        precreated: true,
      });
      setIsLoading(false);
      if (resource) showFeatureResourceNotification(resource);
    } catch (e) {
      setIsLoading(false);
      window.flightLogger.error("[Resources] handleAddUrl failed");
      throw e;
    }
  };

  const showFeatureResourceNotification = (resource: ResourceType) => {
    if (canControlClassroom || activeBreakoutSessionId) {
      createFeatureResourceToast(currentUser, true, resource.title, () => {
        dispatch(
          featureContentInOneUp({
            contentId: resource.id,
            contentType: PaneContentType.Resource,
            breakoutRoomId: breakoutRoom?.id,
            ownerId: currentUserId,
          }),
        );
      });
    } else {
      createResourceUploadedToast(currentUser, resource.title);
    }
  };

  return (
    <>
      <input
        className="display-none"
        type="file"
        accept={ALLOWED_FILE_EXTENSIONS.join(",")}
        ref={fileInputRef}
        onChange={handleUploadFile}
      />
      <Button
        className="mb1 flex-none"
        kind="classroom-secondary"
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        disabled={isLoading}
      >
        <UploadIcon color="white" />
        Upload a File
      </Button>
      <Button
        className="mb1 flex-none"
        kind="classroom-secondary"
        data-testid="add-url-resource"
        onClick={() => setShowAddUrlForm(true)}
        disabled={isLoading}
      >
        <LinkIcon color="white" />
        Add a URL
      </Button>

      {showAddUrlForm && (
        <AddUrlForm handleAddUrl={handleAddUrl} handleClose={() => setShowAddUrlForm(false)} />
      )}
    </>
  );
}

export { NewUrlAndFileResources };
